import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { MSection, SectionHeader } from '@/components/marketing/section'

export const Route = createFileRoute('/_site/integrations/discord')({
  component: DiscordIntegrationPage,
  head: () => ({
    meta: [
      { title: 'Discord Uptime Alerts & Server Monitoring — Uptrack' },
      { name: 'description', content: 'Send uptime alerts to any Discord channel via webhooks. Rich embeds with status, response time, and failure count. Included with every plan.' },
      { property: 'og:title', content: 'Discord Uptime Alerts & Server Monitoring — Uptrack' },
      { property: 'og:description', content: 'Uptime alerts in Discord via webhooks. Rich embeds with full context. Included with every plan.' },
      { property: 'og:image', content: 'https://uptrack.app/og/integrations-discord.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/integrations/discord' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://uptrack.app/integrations' },
            { '@type': 'ListItem', position: 3, name: 'Discord', item: 'https://uptrack.app/integrations/discord' },
          ],
        }),
      },
    ],
  }),
})

function DiscordIntegrationPage() {
  return (
    <>
      {/* Hero */}
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Discord uptime alerts for your server.
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              Running a community, game server, or dev team on Discord? Get downtime alerts as rich embeds — color-coded, with full incident context, right in your channel.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">Included with every plan</p>
            <div className="mt-8">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>

      {/* Why */}
      <MSection tone="raised">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader
              title="Why Discord for monitoring?"
              lead="Discord webhooks are free, fast, and support rich embeds. No bot to host, no permissions to manage. Paste a webhook URL and you are done."
            />
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-background p-6 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-status-down" />
                <span className="font-semibold">Uptrack Alert</span>
              </div>
              <p className="font-semibold text-status-down">api.example.com is DOWN</p>
              <div className="mt-2 grid grid-cols-3 gap-2 text-muted-foreground text-xs">
                <div><span className="text-foreground">Status:</span> 503</div>
                <div><span className="text-foreground">Response:</span> 12,340ms</div>
                <div><span className="text-foreground">Failures:</span> 3</div>
              </div>
            </div>
          </div>
        </div>
      </MSection>

      {/* How to set up */}
      <MSection>
        <SectionHeader title="How to set up Discord alerts" />
        <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { step: '1', title: 'Create a Discord webhook', desc: 'In your Discord server, go to Channel Settings > Integrations > Webhooks > New Webhook. Copy the webhook URL.' },
                { step: '2', title: 'Add to Uptrack', desc: 'In Uptrack, create a new alert channel, select Discord, and paste your webhook URL.' },
                { step: '3', title: 'Assign to monitors', desc: 'Attach the Discord channel to any monitors you want alerts for. Set consecutive failure thresholds to reduce noise.' },
          ].map((item) => (
            <li key={item.step} className="rounded-lg border border-border bg-card p-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background num text-sm font-semibold text-foreground">
                {item.step}
              </span>
              <h3 className="mt-4 text-[17px] font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.desc}</p>
            </li>
          ))}
        </ol>
      </MSection>

      {/* What you get */}
      <MSection tone="raised">
        <SectionHeader title="What you get" />
        <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
          {[
            'Rich embed alerts with color-coded severity',
                  'Status code, response time, and failure count',
                  'Recovery notifications when service is restored',
                  'Simple webhook setup — no bot hosting required',
                  'Multiple Discord channels per account on paid plans',
                  'Works with any Discord server (community, team, personal)',
                  'Available on every plan (one chat channel on the free plan)',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 border-t border-border pt-3 text-[15px] text-body">
              <span aria-hidden="true" className="mt-0.5 font-mono text-status-up">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </MSection>

      {/* CTA */}
      <section>
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <div className="grid grid-cols-1 items-center gap-8 rounded-frame border border-border bg-card p-8 shadow-raised md:p-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Get Discord alerts in under a minute</h2>
              <p className="mt-3 max-w-xl text-lead text-body">
                Every plan includes 30-second checks and alert confirmation.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
